import React, {
  Fragment,
  useRef,
  useMemo,
  useState,
  useContext,
  createContext,
  useEffect,
} from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism.css"; //Example style, you can use another
import toast from "react-hot-toast";
import { Card, Table, Badge, Tooltip, OverlayTrigger } from "react-bootstrap";
import parse from 'html-react-parser';
import loadingCircleImg from "../../../../images/misc/loading-circle-green.svg";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import ErrorBoundary from "../../ErrorBoundary";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import "react-datepicker/dist/react-datepicker.css";

import { ExportJsonCsv } from "react-export-json-csv";
import { Box, Button } from "@mui/material";
import { FileCopyOutlined as FileCopyOutlinedIcon } from "@mui/icons-material";
import { TypeAnimation } from "react-type-animation";
import { copyTextToClipboard, useDocumentTitle } from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import {
  arraysAreEqual,
  epochToDateString,
  epochToDateTimeString,
  stringToRGB,
  toTitleCase,
} from "../../../../utils";
import AddEditDynamicEvent from "./AddEditDynamicEvent";
import imageUploadLoader from "../../../../images/misc/loader-light-blue-bg.gif";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import { getDesignName, EVENTS_PAGE_SIZE } from "./dynamicEventDesigns";

const EMAIL_EDITOR_TYPES = {
  Signup: "signupEmail",
  Approved: "approvedEmail",
  Denied: "deniedEmail",
};

// Small pills next to an event title: which page design it uses, whether an
// A/B test is running, and how many language versions exist.
const EventDesignBadges = ({ event }) => {
  if (!event) return null;
  const design = event.design || {};
  const abTest = design.abTest || {};
  const translations = Array.isArray(event.translations) ? event.translations : [];
  const staleCount = translations.filter((t) => t.stale).length;
  return (
    <span className="event-design-badges">
      {abTest.enabled && Array.isArray(abTest.designIds) && abTest.designIds.length > 1 ? (
        <span
          className="event-badge event-badge--ab"
          title={`A/B test: ${abTest.designIds.map(getDesignName).join(" vs ")}`}
        >
          A/B · {abTest.designIds.map(getDesignName).join(" / ")}
        </span>
      ) : design.designId && design.designId !== "original" ? (
        <span className="event-badge event-badge--design" title="Page design">
          {getDesignName(design.designId)}
        </span>
      ) : null}
      {translations.length ? (
        <span
          className={`event-badge event-badge--lang ${staleCount ? "is-stale" : ""}`}
          title={
            staleCount
              ? `${staleCount} language version${staleCount > 1 ? "s" : ""} out of date`
              : "Language versions"
          }
        >
          EN{translations.map((t) => ` · ${t.lang.toUpperCase()}`).join("")}
          {staleCount ? " ⚠" : ""}
        </span>
      ) : null}
    </span>
  );
};

// Per-design page views / RSVPs / conversion for the selected event. Shown
// when the event runs an A/B test or its RSVPs span more than one design.
const DesignStatsPanel = ({ employee, event, rsvps = [] }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const eventId = event?.eventId;

  const abEnabled = !!event?.design?.abTest?.enabled;
  const designsInRsvps = new Set(rsvps.map((r) => r.designId || "original"));
  const shouldShow = abEnabled || designsInRsvps.size > 1;

  useEffect(() => {
    if (!eventId || !shouldShow) {
      setStats(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const api = new ApiService({ employee });
        const response = await api.get(`landmark/dynamicevents/getDesignStats/${eventId}`);
        if (!cancelled && response && response.success) {
          setStats(response);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, shouldShow, rsvps.length]);

  if (!shouldShow) return null;

  const designs = stats?.designs || [];
  const maxRsvps = Math.max(1, ...designs.map((d) => d.rsvps || 0));
  const maxViews = Math.max(1, ...designs.map((d) => d.uniqueViews || 0));

  return (
    <div className="design-stats">
      <div className="design-stats-head">
        <h5>
          Design performance
          {abEnabled ? <span className="event-badge event-badge--ab">A/B test running</span> : null}
        </h5>
        <div className="example-text">
          Visitors are counted once per browser session. Conversion = RSVPs ÷ unique visitors.
          Views with a <code>?design=</code> override (previews) are not counted.
        </div>
      </div>
      {loading && !stats ? (
        <div className="example-text">Loading…</div>
      ) : designs.length ? (
        <div className="design-stats-grid">
          {designs.map((d) => (
            <div className="design-stat" key={d.designId}>
              <div className="design-stat-name">{getDesignName(d.designId)}</div>
              <div className="design-stat-row">
                <span>Unique visitors</span>
                <b>{d.uniqueViews}</b>
              </div>
              <div className="design-stat-bar">
                <span style={{ width: `${(d.uniqueViews / maxViews) * 100}%` }} />
              </div>
              <div className="design-stat-row">
                <span>RSVPs</span>
                <b>{d.rsvps}</b>
              </div>
              <div className="design-stat-bar design-stat-bar--rsvp">
                <span style={{ width: `${(d.rsvps / maxRsvps) * 100}%` }} />
              </div>
              <div className="design-stat-conv">
                {d.conversionRate === null || d.conversionRate === undefined
                  ? "No visits recorded yet"
                  : `${d.conversionRate}% conversion`}
                <span className="design-stat-sub"> · {d.views} page views</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="example-text">No page views recorded yet.</div>
      )}
      {stats?.languages?.length > 1 ? (
        <div className="design-stats-langs">
          RSVPs by language:{" "}
          {stats.languages.map((l) => (
            <span key={l.lang} className="rsvp-lang-pill">
              {l.lang.toUpperCase()} {l.rsvps}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const PasswordPrompt = ({ employee, setPassword, submitPassword }) => {
  const handleInputChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitPassword(employee);
  };

  return (
    <div className="d-flex">
      <input
        onChange={handleInputChange}
        type="employee"
        className="form-control w-100 mt-1 inline-input"
        placeholder="Password"
        value={employee}
      ></input>
      <div
        className="btn btn-primary light btn-xs mt-1 me-1 w-fc inline-btn"
        onClick={(e) => handleSubmit(e)}
      >
        Submit
      </div>
    </div>
  );
};

const OwnerEmailPrompt = ({
  eventId,
  submitOwnerAssignment,
  setShowEmailEventId,
}) => {
  const [email, setEmail] = useState("");

  const handleInputChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitOwnerAssignment(eventId, email);
    if (result) {
      // reset and hide the email input on success
      setEmail("");
      setShowEmailEventId("");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        onChange={handleInputChange}
        type="email"
        className="form-control w-100 mt-1"
        placeholder="Owner Email"
        value={email}
      ></input>
      <div
        className="btn btn-primary light btn-xs mt-1 me-1 w-fc"
        onClick={(e) => handleSubmit(e)}
      >
        Add
      </div>
    </form>
  );
};

// Change name
const OwnerAssign = ({ employee }) => {
  const [events, setEvents] = useState([]);
  const [showEmailEventId, setShowEmailEventId] = useState("");

  async function getEvents() {
    try {
      const api = new ApiService({ employee });
      const eventsResponse = await api.get(
        `landmark/dynamicevents/getAllEvents`
      );
      console.log(`OwnerAssign - events`, eventsResponse);
      setEvents(eventsResponse);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getEvents();
  }, []);

  async function submitOwnerAssignment(eventId, email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = emailRegex.test(email);
    if (!isValidEmail) {
      toast.error("Please enter a valid email");
      return false;
    }
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        `landmark/dynamicevents/assignOwnerToEvent/`,
        {
          eventId,
          email,
        }
      );
      if (!success) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else {
        toast.success(`${success}`);
        // refresh owners
        getEvents();
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function removeOwnerAssignment(eventId, email) {
    if (!eventId || !email) {
      toast.error("Error occurred");
      return false;
    }
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        `landmark/dynamicevents/removeOwnerFromEvent/`,
        {
          eventId,
          email,
        }
      );
      if (!success) {
        toast.error(`An error occurred: ${error}`);
      } else {
        toast.success(`${success}`);
        // refresh owners
        getEvents();
        return true;
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (!events || !events.length) {
    return <div>Loading Events...</div>;
  }

  return (
    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
      <Card className="mb-3 p-3 h-fc bot-table">
        <div className="row">
          <div className="outputHeader">Event Owners</div>
        </div>
        <div className="events-owners-subheading">
          Owners assigned to events will receive email notifications when new
          event RSVPs are detected
        </div>
        <div className="event-owners-wrapper">
          {events.map((event) => {
            return (
              <div key={event.eventId}>
                <div className="event-owner-container">
                  <img src={event.imageUrl} className="img-fluid event-img" />
                  <div className="event-title">{event.title}</div>
                  {event.owners ? (
                    <>
                      {event.owners.map((o, i) => (
                        <div className="event-owner-outer" key={i}>
                          <div className="event-owner">{o.email}</div>
                          <i
                            onClick={() =>
                              removeOwnerAssignment(event.eventId, o.email)
                            }
                            className="event-owner-remove fa-sharp fa-solid fa-delete-left ml-05"
                          ></i>
                        </div>
                      ))}
                    </>
                  ) : null}
                  {showEmailEventId === event.eventId ? (
                    <motion.div
                      animate={{ opacity: 1 }}
                      initial={{ opacity: 0 }}
                    >
                      <OwnerEmailPrompt
                        eventId={event.eventId}
                        submitOwnerAssignment={submitOwnerAssignment}
                        setShowEmailEventId={setShowEmailEventId}
                      />
                    </motion.div>
                  ) : (
                    <i
                      onClick={() => setShowEmailEventId(event.eventId)}
                      className="event-owner-add fa-sharp fa-solid fa-plus ml-05"
                    ></i>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </motion.div>

    // <Box>
    //   Assign Event
    //   <Select
    //     options={eventsOptions}
    //     onChange={(values) => setSelectedEventId(values)}
    //     color="#fff"
    //     styles={{ backgroundColor: "#000" }}
    //   ></Select>
    //   <input className="form-control" onKeyUp={changeInputText}></input>
    //   <Button className="form-control" onClick={submitOwnerAssignment}>
    //     Submit
    //   </Button>
    // </Box>
  );
};

const SelectEvent = ({ events = [], employee, selectedEvent }) => {
  const [visibleCount, setVisibleCount] = useState(EVENTS_PAGE_SIZE);

  const copyText = (text, desc = null) => {
    copyTextToClipboard(text);
    desc = desc ? desc : "text";
    toast.success(`${desc} copied to clipboard`);
  };

  // Sort by createdTime descending
const sortedEvents = useMemo(() => {
  if (!Array.isArray(events)) return [];
  return [...events].sort(
    (a, b) => (b.createdTime || 0) - (a.createdTime || 0)
  );
}, [events]);

  // Show the most recent EVENTS_PAGE_SIZE; "Show more" adds another page
  const visibleEvents = sortedEvents.slice(0, visibleCount);
  const remainingEvents = Math.max(0, sortedEvents.length - visibleCount);

  return (
    <>
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table">
          <div className="row">
            <div className="outputHeader">VIP Dinner Events</div>
            <>
              <div className="events-owners-subheading d-flex align-items-center justify-content-center">
                <a
                  target="_blank"
                  href="https://docs.google.com/spreadsheets/d/1MC0b2J3mWG5r-RyEsl2TqaQWgc4o2Wem66ZOJlkB5pg/edit?gid=138896324#gid=138896324"
                >
                  <div className="create-event-btn btn btn-sm btn-success btn-view-rsvps">
                    View RSVPs for all events
                  </div>
                </a>
              </div>

              <div
                className="event-owners-wrapper"
                style={{ marginBottom: "12px" }}
              >
                {visibleEvents && visibleEvents.length
                  ? visibleEvents.map((event) => {
                      const selectedClass =
                        selectedEvent &&
                        event.eventId === selectedEvent.eventId
                          ? "selected"
                          : "";
                      return (
                        <div key={event.eventId}>
                          <div className={`event-owner-container ${selectedClass}`}>
                            <img
                              src={event.imageUrl}
                              className="img-fluid event-img"
                            />
                            <div className="event-title">
                              <b>{event.title}</b>{" "}
                              <EventDesignBadges event={event} />
                            </div>
                            <div className="event-title">
                              <a
                                className="blue-link"
                                target="_blank"
                                href={`https://events.landmarkventures.com/${event.pageUrl}`}
                              >
                                {`https://events.landmarkventures.com/${event.pageUrl}`}
                              </a>{" "}
                              <span
                                className="clipboard-icon hvr-shrink"
                                onClick={() =>
                                  copyText(
                                    `https://events.landmarkventures.com/${event.pageUrl}`,
                                    "URL"
                                  )
                                }
                              >
                                <FileCopyOutlinedIcon
                                  style={{
                                    width: "0.7em",
                                    height: "0.7em",
                                  }}
                                />
                              </span>
                            </div>
                            {event.referralUrls && event.referralUrls.length ? (
                              <>
                                <div className="ref-urls-title">
                                  Referral URLs:
                                </div>
                                <div className="ref-urls-container">
                                  {event.referralUrls.map((r) => (
                                    <div
                                      className="ref-inner-container"
                                      key={r.url}
                                    >
                                      <span className="ref-urls-name">
                                        {r.name}:
                                      </span>
                                      <a
                                        className="blue-link ref-urls-url"
                                        target="_blank"
                                        href={`https://events.landmarkventures.com/${event.pageUrl}/${r.url}`}
                                      >{`https://events.landmarkventures.com/${event.pageUrl}/${r.url}`}</a>{" "}
                                      <span
                                        className="clipboard-icon hvr-shrink"
                                        onClick={() =>
                                          copyText(
                                            `https://events.landmarkventures.com/${event.pageUrl}/${r.url}`,
                                            "URL"
                                          )
                                        }
                                      >
                                        <FileCopyOutlinedIcon
                                          style={{
                                            width: "0.7em",
                                            height: "0.7em",
                                          }}
                                        />
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : null}
                            {event.translations && event.translations.length ? (
                              <>
                                <div className="ref-urls-title">Languages:</div>
                                <div className="ref-urls-container">
                                  {event.translations.map((t) => (
                                    <div className="ref-inner-container" key={t.lang}>
                                      <span className="ref-urls-name">
                                        {t.label || t.lang.toUpperCase()}
                                        {t.stale ? (
                                          <span
                                            className="lang-stale-flag"
                                            title="Out of date — regenerate in the editor"
                                          >
                                            {" "}
                                            (out of date)
                                          </span>
                                        ) : null}
                                        :
                                      </span>
                                      <a
                                        className="blue-link ref-urls-url"
                                        target="_blank"
                                        rel="noreferrer"
                                        href={`https://events.landmarkventures.com/${event.pageUrl}?lang=${t.lang}`}
                                      >{`https://events.landmarkventures.com/${event.pageUrl}?lang=${t.lang}`}</a>{" "}
                                      <span
                                        className="clipboard-icon hvr-shrink"
                                        onClick={() =>
                                          copyText(
                                            `https://events.landmarkventures.com/${event.pageUrl}?lang=${t.lang}`,
                                            "URL"
                                          )
                                        }
                                      >
                                        <FileCopyOutlinedIcon
                                          style={{ width: "0.7em", height: "0.7em" }}
                                        />
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  : null}
              </div>
              {remainingEvents > 0 && (
              <div className="events-owners-subheading d-flex align-items-center justify-content-center">
                  <button
                    type="button"
                    className="create-event-btn btn btn-sm btn-primary btn-view-rsvps"
                    onClick={() =>
                      setVisibleCount((c) => c + EVENTS_PAGE_SIZE)
                    }
                  >
                    {`Show more events (${remainingEvents} more)`}
                  </button>
                </div>
                )}
            </>
          </div>
        </Card>
      </motion.div>
    </>
  );
};

const AuditEvents = ({ employee, events }) => {
  const [auditResults, setAuditResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [auditedEvents, setAuditedEvents] = useState([]);
  const [addingMissing, setAddingMissing] = useState({});
  const [addMissingResults, setAddMissingResults] = useState({});

  async function performAudit() {
    try {
      setIsLoading(true);
      const api = new ApiService({ employee });

      let checkAllEventsAfterEvent;
      let filteredEvents = [];

      if (events && events.length >= 15) {
        checkAllEventsAfterEvent = events[events.length - 15].pageUrl;
        filteredEvents = events.slice(-15);
      } else if (events && events.length > 0) {
        checkAllEventsAfterEvent = events[0].pageUrl;
        filteredEvents = [...events]; // use all
      }

      const url = checkAllEventsAfterEvent
        ? `landmark/dynamicevents/performAudit/${checkAllEventsAfterEvent}`
        : `landmark/dynamicevents/performAudit`;

      const auditResponse = await api.get(url);

      setAuditResults(auditResponse);
      setAuditedEvents(filteredEvents);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }
  async function addMissingRsvpsToSheet(event) {
    const pageUrl = event.pageUrl;
    try {
      setAddingMissing((prev) => ({ ...prev, [pageUrl]: true }));
      setAddMissingResults((prev) => ({ ...prev, [pageUrl]: null }));
      const api = new ApiService({ employee });

      // re-adding can take a while (append + formatting per RSVP), so allow more time
      const response = await api.get(
        `landmark/dynamicevents/addMissingRsvpsToSheet/${pageUrl}`,
        null,
        null,
        { timeout: 120000 }
      );

      if (response && response.error) {
        setAddMissingResults((prev) => ({
          ...prev,
          [pageUrl]: { type: "error", message: response.error },
        }));
      } else {
        setAddMissingResults((prev) => ({
          ...prev,
          [pageUrl]: {
            type: "success",
            message: `Added ${response.addedCount || 0}${
              response.failedCount ? `, ${response.failedCount} failed` : ""
            }. Re-running audit...`,
          },
        }));
        // refresh the audit so the list reflects the newly added entries
        await performAudit();
      }
    } catch (error) {
      console.error(error);
      setAddMissingResults((prev) => ({
        ...prev,
        [pageUrl]: { type: "error", message: "Request failed" },
      }));
    } finally {
      setAddingMissing((prev) => ({ ...prev, [pageUrl]: false }));
    }
  }

  const { breakdown = {} } = auditResults;

  return (
    <>
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table">
          <div className="row">
            <div className="outputHeader">Audit Events</div>
            <div className="events-owners-subheading">
              This audit compares Google Sheet entries with those in the
              database to identify any mismatched or missing RSVPs.
              <br />
              <i>
                Only the last 15 events are audited (please contact mjones if
                you need earlier events)
              </i>
            </div>
            <div className="events-owners-subheading">
              {isLoading ? (
                <img
                  src={imageUploadLoader}
                  alt="Loading audit..."
                  width="100"
                  className="img-thumbnail"
                />
              ) : (
                <div
                  className="create-event-btn btn btn-sm btn-success btn-view-rsvps"
                  onClick={performAudit}
                >
                  Perform Audit
                </div>
              )}
            </div>
          </div>
          <div
            className="event-owners-wrapper"
            style={{ marginBottom: "12px" }}
          >
            {Object.keys(breakdown).length > 0 &&
              auditedEvents.map((event) => {
                const audit = breakdown[event.pageUrl];
                if (!audit) return null;

                const {
                  totalRsvpsInDb,
                  totalEmailsInSheet,
                  unsuccessfulCount,
                  unsuccessfulRsvps,
                } = audit;

                const isExpanded = expandedEvents[event.pageUrl];
                const visibleRsvps = isExpanded
                  ? unsuccessfulRsvps
                  : unsuccessfulRsvps.slice(0, 4);

                return (
                  <div key={event.eventId}>
                    <div className="event-owner-container">
                      <div className="event-title">
                        <b>{event.title}</b>
                      </div>
                      <div className="event-title">
                        <a
                          className="blue-link"
                          target="_blank"
                          href={`https://events.landmarkventures.com/${event.pageUrl}`}
                        >
                          {`https://events.landmarkventures.com/${event.pageUrl}`}
                        </a>
                      </div>

                      <div className="text-sm mt-2">
                        <span className="mr-4">
                          <strong>DB:</strong> {totalRsvpsInDb}
                        </span>
                        <span className="mr-4">
                          <strong>Sheet:</strong> {totalEmailsInSheet}
                        </span>
                        <span
                          className={`mr-4 ${
                            unsuccessfulCount > 0
                              ? "text-red-600 font-semibold"
                              : ""
                          }`}
                        >
                          <strong>Unmatched:</strong> {unsuccessfulCount}
                        </span>
                      </div>

                      {unsuccessfulCount > 0 && (
                        <div className="bg-gray-100 rounded p-3 mt-2 text-sm">
                          <div className="font-semibold mb-2 text-sm">
                            Unsuccessful RSVPs:
                          </div>
                          <ul className="space-y-1">
                            {visibleRsvps.map((rsvp, i) => (
                              <li key={i}>
                                <code>{rsvp.email}</code> — {rsvp.reason}
                              </li>
                            ))}
                          </ul>
                          {!isExpanded && unsuccessfulRsvps.length > 4 && (
                            <div
                              className="mt-2 text-xs text-red-600 underline cursor-pointer"
                              onClick={() =>
                                setExpandedEvents((prev) => ({
                                  ...prev,
                                  [event.pageUrl]: true,
                                }))
                              }
                            >
                              View more ({unsuccessfulRsvps.length - 4}{" "}
                              remaining)
                            </div>
                          )}

                          {unsuccessfulRsvps.some(
                            (rsvp) => rsvp.reason === "Email not found in sheet"
                          ) && (
                            <div className="mt-3">
                              {addingMissing[event.pageUrl] ? (
                                <img
                                  src={imageUploadLoader}
                                  alt="Adding missing RSVPs..."
                                  width="80"
                                  className="img-thumbnail"
                                />
                              ) : (
                                <div
                                  className="create-event-btn btn btn-sm btn-success btn-view-rsvps"
                                  onClick={() => addMissingRsvpsToSheet(event)}
                                >
                                  Add missing entries to Google Sheet
                                </div>
                              )}
                              {addMissingResults[event.pageUrl] && (
                                <div
                                  className={`mt-2 text-xs ${
                                    addMissingResults[event.pageUrl].type ===
                                    "error"
                                      ? "text-red-600"
                                      : "text-green-700"
                                  }`}
                                >
                                  {addMissingResults[event.pageUrl].message}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {unsuccessfulCount === 0 && (
                        <div className="text-green-700 text-sm mt-2">
                          ✅ No issues found for this event.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      </motion.div>
    </>
  );
};

const ConfirmEmailTemplateEditor = ({
  event,
  fetchEvents,
  employee,
  emailType,
}) => {
  const [emailSubject, setEmailSubject] = useState(
    event ? event[emailType].emailSubject : ""
  );
  const [emailBody, setEmailBody] = useState(
    event ? event[emailType].emailBody : ""
  );
  const [emailSendFrom, setEmailSendFrom] = useState(
    event ? event[emailType].emailSendFrom : ""
  );
  const [emailSendFromName, setEmailSendFromName] = useState(
    event ? event[emailType].emailSendFromName : ""
  );
  useEffect(() => {
    // new option created
    if (!event) {
      setEmailSubject("");
      setEmailBody("");
      setEmailSendFrom("");
      setEmailSendFromName("");
    }
    // existing option obj
    else {
      setEmailSubject(event[emailType].emailSubject);
      setEmailBody(event[emailType].emailBody);
      setEmailSendFrom(event[emailType].emailSendFrom);
      setEmailSendFromName(event[emailType].emailSendFromName);
    }
  }, [event]);

  console.log("event", event);
  console.log("event[emailType]", event[emailType]);

  const emailHtml = `${emailBody
    .replace(`{FirstName|"There"}`, "There")}`;

  async function editTemplateEmail() {
    try {
      const api = new ApiService({ employee });
      const obj = {
        eventId: event.eventId,
      };
      obj[emailType] = {
        emailSubject,
        emailBody,
        emailSendFrom,
        emailSendFromName,
      };
      const eventsResponse = await api.post(
        "landmark/dynamicevents/editEventEmailTemplate",
        obj
      );
      console.log(
        `editTemplateEmail() - landmark/dynamicevents/editEventEmailTemplate - response`,
        eventsResponse
      );
      const { success, error } = eventsResponse;
      if (!success) {
        toast.error("An error occurred");
      } else if (error) {
        toast.error(error);
      } else {
        toast.success(success);
        // refresh events
        fetchEvents();
      }
    } catch (error) {
      console.error(error);
    }
  }

  let title = "";
  let description = "";
  if (emailType === EMAIL_EDITOR_TYPES.Approved) {
    title = "Confirmation Email Template Editor";
    description =
      "These emails are able to be sent when an RSVP has been approved for at least one event";
  } else if (emailType === EMAIL_EDITOR_TYPES.Denied) {
    title = "Denied Email Template Editor";
    description =
      "These emails are able to be sent when an RSVP has been denied for all requested events";
  } else if (emailType === EMAIL_EDITOR_TYPES.Signup) {
    title = "Signup Email Template Editor";
    description =
      "These emails are automatically sent after a user submits an invitation request";
  }

  return (
    <div className="confirm-email-editor-container">
      <div className="title">{title}</div>
      <div className="description">{description}</div>
      <div className="row">
        <div className="col-6">
          <div className="subtitle">Edit Template</div>
          <div className="template-form">
            <div className="row">
              <div className="col-12">
                <button
                  className={`create-event-btn save-changes-btn btn btn-sm btn-primary`}
                  onClick={editTemplateEmail}
                >
                  {`Save Changes`}
                  <i className="event-owner-add fa-sharp fa-solid fa-check ml-05"></i>
                </button>
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Send From Email{" "}
                  <span className="example-text">
                    (non landmark emails need to be configured first - contact
                    mjones@landmarkventures.com)
                  </span>
                </label>
                <input
                  type="text"
                  value={emailSendFrom}
                  className="form-control"
                  onChange={(e) => setEmailSendFrom(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Send From Name{" "}
                  {/* <span className="example-text">e.g. Andrew Kalaigian</span> */}
                </label>
                <input
                  type="text"
                  value={emailSendFromName}
                  className="form-control"
                  onChange={(e) => setEmailSendFromName(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Subject <span className="example-text"></span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  className="form-control"
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>
              <div className="form-group mb-3 col-12">
                <label className="form-label">
                  Body <span className="example-text"></span>
                </label>
                {/* <textarea
                  value={emailBody}
                  className="form-control textarea rmono"
                  rows="20"
                  onChange={(e) => setEmailBody(e.target.value)}
                /> */}

                <ErrorBoundary>
                  <Editor
                    value={emailBody}
                    onValueChange={(code) => setEmailBody(code)}
                    highlight={(code) => {
                      // console.log("Code to highlight:", code);
                      try {
                        return highlight(code, languages.markup);
                      } catch (error) {
                        console.error("Highlighting error:", error);
                        return code; // Return unformatted code as a fallback
                      }
                    }}
                    padding={15}
                    className="code-editor"
                    style={{
                      color: "#000",
                      fontSize: 11,
                      backgroundColor: "#fff",
                      border: "1px #0072ff solid",
                      borderRadius: "0.625rem",
                      fontFamily:
                        "ui-monospace,Roboto Mono,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                    }}
                  />
                </ErrorBoundary>

                {/* <CodeEditor
                  value={emailBody}
                  language="html"
                  onChange={(evn) => setEmailBody(evn.target.value)}
                  padding={15}
                  data-color-mode="light"
                  style={{
                    color: "#000",
                    fontSize: 11,
                    backgroundColor: "#fff",
                    border: "1px #0072ff solid",
                    borderRadius: "0.625rem",
                    fontFamily:
                      "ui-monospace,Roboto Mono,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                  }}
                /> */}
              </div>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="subtitle">Viewer</div>
          <div className="viewer">
            <div className="subject">{emailSubject}</div>
            <div className="divider"></div>
            <div className="body">{parse(emailHtml)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EventRsvpsViewer = ({ employee, events = [], setEvents }) => {
  const [selectedEventId, setSelectedEventId] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]); // keep as array
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({}); // { [rsvpId]: true }
  const [rsvpConfirmationEmails, setRsvpConfirmationEmails] = useState([]);
  const [rsvpDeniedEmails, setRsvpDeniedEmails] = useState([]);
    const [showSignupEmailEditor, setShowSignupEmailEditor] = useState(false);
    const [showApprovedEmailEditor, setShowApprovedEmailEditor] = useState(false);
    const [showDeniedEmailEditor, setShowDeniedEmailEditor] = useState(false);

    function setShowEmailEditor(type, show) {
    setShowSignupEmailEditor(false);
    setShowApprovedEmailEditor(false);
    setShowDeniedEmailEditor(false);

    if (!show) {
      return;
    }

    if (type === EMAIL_EDITOR_TYPES.Approved) {
      setShowApprovedEmailEditor(true);
    } else if (type === EMAIL_EDITOR_TYPES.Denied) {
      setShowDeniedEmailEditor(true);
    } else if (type === EMAIL_EDITOR_TYPES.Signup) {
      setShowSignupEmailEditor(true);
    }
  }

  useEffect(() => {
    if (!selectedEvent || !selectedEvent.eventId) return; // <- bail out

    setSelectedEventId(selectedEvent.eventId);
    console.log("selectedEvent", selectedEvent);
    fetchConfirmEmails();
    fetchDeniedEmails();
  }, [selectedEvent]);

    async function fetchEvents() {
    try {
      const api = new ApiService({ employee });
      const eventsResponse = await api.get(`landmark/dynamicevents/getAllEvents`);
      console.log(`fetchEvents() - events`, eventsResponse);
      setEvents(eventsResponse);

      // refresh selectedEvent (so it displays latest data)
      const _selectedEventId =
        selectedEvent && selectedEvent.eventId ? selectedEvent.eventId : null;
      if (_selectedEventId) {
        const newSelectedEvent = eventsResponse.find(
          (e) => e.eventId === _selectedEventId
        );
        if (newSelectedEvent) {
          setSelectedEvent(newSelectedEvent);
        }
      }
      //setSelectedEventId(eventsResponse[0].eventId);
    } catch (error) {
      console.error(error);
    }
  }

    async function fetchConfirmEmails() {
    try {
      if (!selectedEvent || !selectedEvent.eventId) {
        console.log(`fetchConfirmEmails() - invalid selected event`);
        return;
      }
      const api = new ApiService({ employee });
      const eventId = selectedEvent.eventId;
      const resp = await api.get(
        `landmark/dynamicevents/getRsvpConfirmationEmails/${eventId}`
      );
      console.log(`fetchConfirmEmails() - rsvpConfirmationEmails`, resp);
      setRsvpConfirmationEmails(resp);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchDeniedEmails() {
    try {
      if (!selectedEvent || !selectedEvent.eventId) {
        console.log(`fetchDeniedEmails() - invalid selected event`);
        return;
      }
      const api = new ApiService({ employee });
      const eventId = selectedEvent.eventId;
      const resp = await api.get(
        `landmark/dynamicevents/getRsvpDeniedEmails/${eventId}`
      );
      console.log(`fetchDeniedEmails() - rsvpDeniedEmails`, resp);
      setRsvpDeniedEmails(resp);
    } catch (error) {
      console.error(error);
    }
  }

  const sendConfirmationEmail = async (rsvpId) => {
    try {
      if (!employee) {
        toast.error(`An error occurred: employee not logged in`);
        return;
      }
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/dynamicevents/sendRsvpConfirmationEmail`,
        {
          rsvpId,
          employee,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        // refresh confirm emails
        fetchConfirmEmails();
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const sendDeniedEmail = async (rsvpId) => {
    try {
      if (!employee) {
        toast.error(`An error occurred: employee not logged in`);
        return;
      }
      const api = new ApiService({ employee, employee });
      const { success, error, info } = await api.post(
        `landmark/dynamicevents/sendRsvpDeniedEmail`,
        {
          rsvpId,
          employee,
        }
      );
      if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        // refresh denied emails
        fetchDeniedEmails();
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  // Auto-select first event when events load
  // useEffect(() => {
  //   if (!selectedEventId && events?.length) {
  //     setSelectedEventId(events[0].eventId);
  //   }
  // }, [events, selectedEventId]);

  useEffect(() => {
    const ev =
      events && events.length
        ? events.find((e) => e.eventId === selectedEventId)
        : null;
    setSelectedEvent(ev);
    if (!ev) {
      setRsvps([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const api = new ApiService({ employee });
        const list = await api.get(
          `landmark/dynamicevents/getRsvps/${ev.eventId}`
        );
        setRsvps(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load RSVPs");
        setRsvps([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedEventId, events, employee]);

  const onSendConfirmation = async (rsvp) => {
    const key = rsvp?.rsvpId || rsvp?.email;
    setSending((m) => ({ ...m, [key]: true }));
    try {
      // TODO: wire to real endpoint later
      const api = new ApiService({ employee });
      const { success, error, info } = await api.post(`landmark/dynamicevents/sendRsvpConfirmationEmail`, { employee: employee, rsvpId: rsvp.rsvpId });
       if (!success && !info) {
        toast.error(`An error occurred: ${error}`);
        return false;
      } else if (success) {
        toast.success(`${success}`);
        // refresh confirm emails
        fetchConfirmEmails();
        return true;
      } else if (info) {
        toast(`${info}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to send confirmation (stub)");
    } finally {
      setSending((m) => {
        const copy = { ...m };
        delete copy[key];
        return copy;
      });
    }
  };

    const emailEditorIsOpen =
    showApprovedEmailEditor || showDeniedEmailEditor || showSignupEmailEditor;

  return (
    <>
      <div className="mb-3 d-flex align-items-center justify-content-center" style={{ gap: 8 }}>
        <label className="form-label mb-0">Select Event to View RSVPs:</label>
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 420 }}
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          <option value="">-- Choose an event --</option>
          {events?.slice().reverse().map((ev) => (
            <option key={ev.eventId} value={ev.eventId}>
              {ev.title || ev.pageUrl || ev.eventId}
            </option>
          ))}
        </select>
        {loading && (
          <span className="ms-2">
            <span className="spinner-border spinner-border-sm" /> Loading…
          </span>
        )}
      </div>

      {selectedEvent ? (
        rsvps.length ? (
          <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
            <Card className="mb-3 h-fc bot-table">
              {selectedEvent.imageUrl ? (
                <div className="event-image-img">
                  <img src={selectedEvent.imageUrl} className="img-fluid" alt="" />
                </div>
              ) : null}

              <div className="row">
                 
                <div className="outputHeader">{selectedEvent.title} RSVPs</div>
                <div className="events-owners-subheading">
                  Browse RSVPs received for {selectedEvent.title}
                </div>
<div className="col-12">
                                <div className="add-edit-btn-container">
                                  {/* {!emailEditorIsOpen || showSignupEmailEditor ? (
                                    <button
                                      className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                                      onClick={() =>
                                        setShowEmailEditor(
                                          EMAIL_EDITOR_TYPES.Signup,
                                          !showSignupEmailEditor
                                        )
                                      }
                                    >
                                      {showSignupEmailEditor
                                        ? `Hide Editor`
                                        : `Edit Signup Email Template`}
                                      <i className="event-owner-add fa-sharp fa-solid fa-envelope ml-05"></i>
                                    </button>
                                  ) : null} */}
                                  {!emailEditorIsOpen || showApprovedEmailEditor ? (
                                    <button
                                      className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                                      onClick={() =>
                                        setShowEmailEditor(
                                          EMAIL_EDITOR_TYPES.Approved,
                                          !showApprovedEmailEditor
                                        )
                                      }
                                    >
                                      {showApprovedEmailEditor
                                        ? `Hide Editor`
                                        : `Edit Confirmation Email Template`}
                                      <i className="event-owner-add fa-sharp fa-solid fa-envelope ml-05"></i>
                                    </button>
                                  ) : null}
                                  {/* {!emailEditorIsOpen || showDeniedEmailEditor ? (
                                    <button
                                      className={`create-event-btn btn btn-sm btn-primary email-options-btn`}
                                      onClick={() =>
                                        setShowEmailEditor(
                                          EMAIL_EDITOR_TYPES.Denied,
                                          !showDeniedEmailEditor
                                        )
                                      }
                                    >
                                      {showDeniedEmailEditor
                                        ? `Hide Editor`
                                        : `Edit Denied Email Template`}
                                      <i className="event-owner-add fa-sharp fa-solid fa-envelope ml-05"></i>
                                    </button>
                                  ) : null} */}
                                </div>
                                {showApprovedEmailEditor && (
                                  <div className="table-wrapper">
                                    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                                      <ConfirmEmailTemplateEditor
                                        employee={employee}
                                        event={selectedEvent}
                                        fetchEvents={fetchEvents}
                                        emailType={EMAIL_EDITOR_TYPES.Approved}
                                      />
                                    </motion.div>
                                  </div>
                                )}
                                {showDeniedEmailEditor && (
                                  <div className="table-wrapper">
                                    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                                      <ConfirmEmailTemplateEditor
                                        employee={employee}
                                        event={selectedEvent}
                                        fetchEvents={fetchEvents}
                                        emailType={EMAIL_EDITOR_TYPES.Denied}
                                      />
                                    </motion.div>
                                  </div>
                                )}
                                {showSignupEmailEditor && (
                                  <div className="table-wrapper">
                                    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                                      <ConfirmEmailTemplateEditor
                                        employee={employee}
                                        event={selectedEvent}
                                        fetchEvents={fetchEvents}
                                        emailType={EMAIL_EDITOR_TYPES.Signup}
                                      />
                                    </motion.div>
                                  </div>
                                )}
                              </div>
                <div className="outputBody">
                  <DesignStatsPanel
                    employee={employee}
                    event={selectedEvent}
                    rsvps={rsvps}
                  />
                  <div className="table-wrapper">
                    <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                      <Table responsive className="header-border ">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Company</th>
                            <th>Job Title</th>
                            <th>Dietary Restrictions</th>
                            <th>Design</th>
                            <th>Lang</th>
                            <th>Received</th>
                            <th>Confirmation Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rsvps.map((r, i) => {
                            const key = r.rsvpId || r.email || i;
                            const name =
                              r.fullName ||
                              [r.firstName, r.lastName].filter(Boolean).join(" ") ||
                              "(No name)";
                            const received = epochToDateTimeString(r.createdTime);
                            const isSending = !!sending[key];
                            const confirmation = rsvpConfirmationEmails
                              .slice()
                              .find((e) => e.rsvpEventId === r.rsvpId);


                            return (
                              <tr key={`rsvp_${key}`}>
                                <td className="wrap" style={{ maxWidth: 550, minWidth: 300 }}>
                                  <div className="user-container">
                                    <div className="name">{name}</div>
                                    {r.email ? (
                                      <div className="email">{r.email}</div>
                                    ) : (
                                      <div className="email text-muted">—</div>
                                    )}
                                  </div>
                                </td>
                                <td className="wrap" style={{ maxWidth: 250 }}>
                                  {r.company || "—"}
                                </td>
                                <td className="wrap" style={{ maxWidth: 250 }}>
                                  {r.jobTitle || "—"}
                                </td>
                                <td className="wrap" style={{ maxWidth: 300 }}>
                                  {r.dietaryRestrictions || "None"}
                                </td>
                                <td className="wrap" style={{ whiteSpace: "nowrap" }}>
                                  <span className="rsvp-design-pill">
                                    {getDesignName(r.designId || "original")}
                                  </span>
                                </td>
                                <td className="wrap">
                                  <span className="rsvp-lang-pill">
                                    {(r.lang || "en").toUpperCase()}
                                  </span>
                                </td>
                                <td className="wrap" style={{ maxWidth: 200 }}>
                                  {received}
                                </td>
                                <td>
                                 {confirmation ? (
                                  <div className="mt-1">
                                    <div>
                                      ✅ Confirmation email sent on{" "}
                                      {epochToDateTimeString(confirmation.sentTime)}
                                    </div>
                                    <div
                                      style={{
                                        textDecoration: "underline",
                                        cursor: isSending ? "default" : "pointer",
                                        color: "#0d6efd",
                                        marginTop: "2px",
                                      }}
                                      onClick={() => !isSending && onSendConfirmation(r)}
                                    >
                                      {isSending ? "Sending again…" : "Send again"}
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => onSendConfirmation(r)}
                                    disabled={isSending}
                                  >
                                    {isSending ? "Sending…" : "Send Confirmation"}
                                  </button>
                                )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </motion.div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ) : (
          <div className="no-rsvps-msg">{loading ? "Loading RSVPs" : "No RSVPs found"}</div>
        )
      ) : (
        // <div className="text-muted">Choose an event to view RSVPs.</div>
        <></>
      )}
    </>
  );
};




const Dashboard = (props) => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState(null);

  useDocumentTitle("VIP Dinner Events Dashboard - Landmark Ventures");

  // Scope dashboard-wide style overrides (e.g. text selection colours) to the
  // body so they also reach modals/toasts that are portalled outside the wrapper
  useEffect(() => {
    document.body.classList.add("vip-dinner-dashboard");
    return () => document.body.classList.remove("vip-dinner-dashboard");
  }, []);

  // set light theme once
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // touch lastSeenTime when authenticated (fire & forget)
  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get(`landmark/employees/login`);
    } catch (error) {
      console.warn("employee login touch failed", error);
    }
  }, [isAuthenticated, employee]);

  async function getEvents() {
    try {
      const api = new ApiService({ employee });
      const eventsResponse = await api.get(`landmark/dynamicevents/getAllEvents`);
      console.log(`Dashboard - getEvents()`, eventsResponse);
      setEvents(eventsResponse);
      setSelectedEvent(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load events");
    }
  }

  // load data once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      getEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // gate while AuthContext initializes
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper dynamic-events">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "VIP Dinner Events Dashboard", url: `/landmark/vipdinner/dashboard` },
          ]}
        />

        {isAuthenticated ? (
          <>
            <AddEditDynamicEvent
              events={events}
              getEvents={getEvents}
              employee={employee}
            />
            {/* <OwnerAssign employee={employee} /> */}
            <SelectEvent
              events={events}
              getEvents={getEvents}
              employee={employee}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
            />
            <AuditEvents
              events={events}
              getEvents={getEvents}
              employee={employee}
            />
            <EventRsvpsViewer
              events={events || []}
              getEvents={getEvents}
              setEvents={setEvents}
              employee={employee}
            />
            {/* {selectedEvent && (
              <MasterEventsTable
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                employee={employee}
              />
            )} */}
          </>
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the dashboard.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Dashboard;